import { useEffect, useState, useContext, useDebugValue } from "react";
import { useTranslation } from "react-i18next";

import useRequest from "../utils/useRequest";
import { toMoney } from "../utils/toMoney";
import { formatDate } from "../utils/toDate";
import { sanitizeNumber } from "../utils/sanitizeNumber";

import { DataContext } from "../utils/DataContext";

import searchIcon from "../assets/search_icon.png";
import debtIcon from "../assets/debt_icon.png";
import paymentIcon from "../assets/payment_icon.png";

import RoundedTable, { TABLE_CELL_TYPES } from "./table-component/RoundedTable";
import Modal from "./Modal";
import Loader from "./Loader";
import TextDiv from "./TextDiv";
import BalanceBoxes from "./BalanceBoxes";
import InformationBox from "./InformationBox";
import CustomInput from "./CustomInput";
import Dropdown from "./Dropdown";
import CustomButton from "./CustomButton";
import SearchFilter from "./SearchFilter";
import PaymentModal from "./PaymentModal";
import { UserAuth } from "../utils/AuthContext";
import { pdf } from "@react-pdf/renderer";
import useLanguage from "../utils/useLanguage";
import TransactionStatementPDF from "./TransactionPdfTemplate";
import downloadIcon from "../assets/download-icon.svg";
import axios from "axios";

const TransactionsModal = ({
  selected,
  handleToggleTransactionsModal,
  withSubCompany,
  setShouldGetCustomers,
}) => {
  const { currentDirection } = useLanguage();
  const { t } = useTranslation("general");

  const { userData } = useContext(UserAuth);

  const transactionType =
    userData?.organizationType === "PAPERFACTORY"
      ? "sellInvoice"
      : "multiContainersInvoice";

  const {
    subCompanies,
    getSubCompanies,
    selectedSubCompany,
    handleSelectSubCompany,
  } = useContext(DataContext);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  const [paymentModal, setPaymentModal] = useState(false);
  const [debtModal, setDebtModal] = useState(false);
  const [detailsModal, setDetailsModal] = useState(false);

  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const { loading, error, data, refetch } = useRequest(
    `/getCustomer/${selected?.id}`,
    "GET",
    null,
    { page: 1, limit: 5 },
    false
  );

  // Search states
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("invoiceNumber"),
    value: "invoiceNumber",
  });
  const [searchQuery, setSearchQuery] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [amounts, setAmounts] = useState({
    total: selected?.total,
    balance: selected?.balance,
    received: selected?.received,
  });

  const [selectedTransactionType, setSelectedTransactionType] = useState({
    value: "",
    title: t("selectAll"),
  });

  const transactionTypeOptions = [
    { value: "debtRepayment", title: t("debtRepayment") },
    { value: "sellInvoice", title: t("addDebt") },
    { value: "", title: t("selectAll") },
  ];

  const searchTitles = [
    {
      title: t("invoiceNumber"),
      value: "invoiceNumber",
    },
    { title: t("Date"), value: "date" },
    { title: t("amount"), value: "amount" },
    { title: t("bankName"), value: "bankName" },
    { title: t("transactionType"), value: "transactionType" },
  ];

  if (withSubCompany) {
    searchTitles.push({ title: t("subCompany"), value: "subCompany" });
  }

  const handleSearchType = async (type) => {
    if (
      type.value === "invoiceNumber" ||
      type.value === "amount" ||
      type.value === "subCompany" ||
      type.value === "bankName" ||
      type.value === "transactionType"
    ) {
      setStartDate(formatDate(new Date()));
      setEndDate(formatDate(new Date()));
    } else if (type.value === "date") {
      setSearchQuery("");
    } else {
      throw error("invalid search type");
    }

    setSelectedTitle(type);
  };

  const handleSearch = async (query = searchQuery) => {
    if (typeof query !== "string") {
      query = searchQuery;
    }

    let searchBy;

    if (selectedTitle?.value === "date") {
      searchBy = { from: startDate, to: endDate };
    } else if (selectedTitle?.value === "amount") {
      searchBy = sanitizeNumber(query);
    } else if (selectedTitle?.value === "transactionType") {
      searchBy = selectedTransactionType?.value;
    } else if (selectedTitle?.value === "subCompany") {
      searchBy = selectedSubCompany?.id;
    } else {
      searchBy = query;
    }

    refetch({
      searchQuery: searchBy,
      searchType: selectedTitle.value,
    });
  };

  // function to fetch all transactions for PDF generation
  const handleDownloadPDF = async () => {
    setPdfLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getCustomer/${selected?.id}`,
        {
          params: {
            searchQuery:
              selectedTitle.value === "date"
                ? { from: startDate, to: endDate }
                : selectedTitle.value === "amount"
                ? sanitizeNumber(searchQuery)
                : // : selectedTitle.value === "subCompany"
                // ? selectedSubCompany?.id
                selectedTitle.value === "transactionType"
                ? selectedTransactionType?.value
                : searchQuery,
            searchType: selectedTitle.value,
            page: 1,
            limit: 10000,
          },
          headers: {
            "organization-id": userData?.organizationId,
          },
        }
      );

      if (response?.data?.transactions) {
        const blob = await pdf(
          <TransactionStatementPDF
            language={currentDirection === "rtl" ? "AR" : "EN"}
            companyName={
              userData?.subCompany?.name ||
              localStorage.getItem("organizationName") ||
              ""
            }
            data={{
              customer: selected,
              transactions: response.data.transactions,
            }}
            transactions={response.data.transactions.records.map(
              (transaction) => ({
                ...transaction,
                name: transaction.name?.split("---")?.[0]
                  ? t(transaction.name?.split("---")?.[0])
                  : transaction.name,
                type: t(transaction.type),
              })
            )}
          />
        ).toBlob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${response.data?.customer?.name || "transactions"}-${
          new Date().getMonth() + 1
        }-${new Date().getDay() + 1}-${(Math.random() * 1000).toFixed(0)}.pdf`;
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 5000);
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  useEffect(() => {
    refetch({
      page: pagination.currentPage,
      limit: 5,
    });
  }, [pagination.currentPage]);

  useEffect(() => {
    if (data?.transactions) {
      setPagination({
        currentPage: data?.transactions?.pagination?.currentPage,
        totalPages: data.transactions.pagination?.totalPages,
        totalRecords: data.transactions?.pagination?.totalRecords,
      });
    }

    setAmounts({
      total: data?.transactions?.total,
      balance: data?.transactions?.balance,
      received: data?.transactions?.received,
    });
  }, [data]);

  const tableTitles = [
    {
      value: "transaction",
    },
    {
      value: "Date",
      keepOnMobile: true,
    },

    {
      value: "amount",
      keepOnMobile: true,
    },
  ];

  const tableRows = data?.transactions?.records?.map((el) => {
    const storedName = el?.name?.split("---")?.[0];

    // To translate the "addDebt"
    const notInvoiceNumber = storedName === "addDebt";
    const isAddDebt = el?.name?.startsWith("addDebt");

    const transactionName = notInvoiceNumber
      ? t(storedName)
      : storedName || t("debtRepayment");

    const bankName = el?.bankName;

    // List of transaction.type for negative style
    const invoiceTypes = ["multiContainersInvoice", "sellInvoice"];

    return [
      {
        value: (
          <div className="cell ">
            <CustomButton
              variant="transparent"
              onClick={() => {
                setSelectedTransaction({ ...el, transactionName });
                setDetailsModal(true);
              }}
              content={
                <div className="o-column o-column--gap-size_100 u-padding_300">
                  <div>{transactionName}</div>
                  {bankName && (
                    <span className="t-text-mini t-bold_normal">
                      ( {bankName} )
                    </span>
                  )}
                </div>
              }
            />
          </div>
        ),
        type: TABLE_CELL_TYPES.ELEMENT,
        keepOnMobile: true,
      },
      { value: el.date, type: TABLE_CELL_TYPES.DATE, keepOnMobile: true },
      {
        value: el.amount,
        type: TABLE_CELL_TYPES.MONEY,
        sign: isAddDebt
          ? "negative-value"
          : el?.type === "debtRepayment"
          ? "positive-value"
          : "",
        bold: true,
        keepOnMobile: true,
        currency: selected?.currency || t("USD"),
      },
    ];
  });

  const TextDivStyles =
    "o-row o-row--vertical-align_center o-row--gap-size_500 ";
  const TextPStyles = "t-bold_semi-bold u-width-specific_150 u-margin_0";

  return (
    <>
      <Modal
        title={t("transactions")}
        onClose={handleToggleTransactionsModal}
        beforeFooterChildren={
          <BalanceBoxes
            noStyle=""
            boxes={[
              {
                title: "balance",
                value: amounts?.balance,
                currency: selected?.currency || t("USD"),
              },
            ]}
          />
        }
      >
        {/* {error && <ErrorBox>{errorMessage}</ErrorBox>} */}
        <section className="o-column-row-switcher o-row--horizontal-align_space-between u-padding--top_200">
          <TextDiv
            title="customer"
            value={selected?.name}
            divClasses="o-row o-row--gap-size_300 o-row--vertical-align_center"
          />
          {userData?.userType === "orgUser" && (
            <div className="o-row o-row--gap-size_300">
              <CustomButton
                variant="green"
                title={t("payment")}
                onClick={() => setPaymentModal((prev) => !prev)}
                icon={paymentIcon}
                iconSize={22}
              />
              <CustomButton
                variant="red"
                title={t("addDebt")}
                onClick={() => setDebtModal((prev) => !prev)}
                icon={debtIcon}
                iconSize={20}
              />
            </div>
          )}
        </section>
        {data?.length !== 0 ? (
          <div className="o-column o-column--gap-size_300">
            <label className="t-bold_semi-bold">{`${t("searchTitle")} ${t(
              selectedTitle?.title
            )}`}</label>
            <section className="o-column-row-switcher o-row--gap-size_300 o-column--vertical-align_end">
              <div className="o-row o-row--vertical-align_center o-column--vertical-align_end o-row--gap-size_300 u-width_full-width o-column--horizontal-align_space-between">
                {selectedTitle.value === "date" ? (
                  <div className="o-column-row-switcher o-column--gap-size_300 ">
                    <CustomInput
                      name="date"
                      title={t("from")}
                      type="date"
                      thick
                      onChange={(date) => {
                        setStartDate(date?.target?.value);
                      }}
                      resetSearch={() => {
                        setStartDate("");
                      }}
                      value={formatDate(startDate)}
                    />

                    <CustomInput
                      name="date"
                      title={t("to")}
                      type="date"
                      thick
                      onChange={(date) => {
                        setEndDate(date?.target?.value);
                      }}
                      resetSearch={() => {
                        setEndDate("");
                      }}
                      value={formatDate(endDate)}
                    />
                  </div>
                ) : selectedTitle.value === "transactionType" ? (
                  <Dropdown
                    options={[...transactionTypeOptions]}
                    selected={selectedTransactionType}
                    setSelected={setSelectedTransactionType}
                    thick
                    // nameInAr={true}
                  />
                ) : selectedTitle.value === "subCompany" ? (
                  <Dropdown
                    options={[{ id: "", nameInAr: "All" }, ...subCompanies]}
                    selected={selectedSubCompany}
                    setSelected={(subCompany) =>
                      handleSelectSubCompany(subCompany, "noRequest")
                    }
                    thick
                    nameInAr={true}
                    getData={getSubCompanies}
                  />
                ) : (
                  <CustomInput
                    placeholder={`${t("searchTitle")} ${t(
                      selectedTitle?.value
                    )}`}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    resetSearch={() => {
                      setSearchQuery(null);
                      // setSearch;
                      handleSearch("");
                    }}
                    thick
                    flex
                  />
                )}
                <SearchFilter
                  options={searchTitles}
                  selected={selectedTitle}
                  setSelected={handleSearchType}
                  thick
                />
              </div>

              <CustomButton
                title={t("search")}
                onClick={handleSearch}
                icon={searchIcon}
                iconSize={15}
                classList="c-search-button"
              />
            </section>
          </div>
        ) : null}
        {loading ? (
          <Loader />
        ) : (
          <>
            {tableRows?.length ? (
              <section>
                <RoundedTable
                  titles={tableTitles}
                  rows={tableRows}
                  pagination={pagination}
                  setPagination={setPagination}
                />
                {pdfLoading && <Loader />}
                <div className="o-row o-row--horizontal-align_end u-margin--top_500 u-margin--bottom_400">
                  <CustomButton
                    variant="blank"
                    onClick={handleDownloadPDF}
                    disabled={pdfLoading}
                    icon={downloadIcon}
                    title={
                      pdfLoading ? t("generatingPdf") : t("downloadStatement")
                    }
                  />
                </div>
              </section>
            ) : (
              <InformationBox>{t("noTransactions")}</InformationBox>
            )}
          </>
        )}

        {paymentModal ? (
          <PaymentModal
            selected={selected}
            handleTogglePaymentModal={() => setPaymentModal((prev) => !prev)}
            modalSize="c-modal--full-height"
            refetchData={refetch}
            setShouldGetCustomers={setShouldGetCustomers}
          />
        ) : null}
        {debtModal ? (
          <PaymentModal
            title="addDebt"
            selected={selected}
            handleTogglePaymentModal={() => setDebtModal((prev) => !prev)}
            modalSize="c-modal--full-height"
            transactionType={transactionType}
            refetchData={refetch}
            setShouldGetCustomers={setShouldGetCustomers}
          />
        ) : null}

        {detailsModal ? (
          <Modal
            modalSize="c-modal--small"
            title="details"
            onClose={() => setDetailsModal(false)}
          >
            <div className="o-column o-column--gap-size_300">
              <TextDiv
                title="transaction"
                value={selectedTransaction?.transactionName}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />

              <TextDiv
                title="amount"
                value={`${toMoney(selectedTransaction?.amount)} ${
                  selected?.currency || t("USD")
                } `}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />

              <TextDiv
                title="Date"
                value={formatDate(selectedTransaction?.date)}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />

              {selectedTransaction?.bankName && (
                <TextDiv
                  title="bankName"
                  value={selectedTransaction?.bankName}
                  divClasses={TextDivStyles}
                  pClasses={TextPStyles}
                />
              )}

              <TextDiv
                title="notes"
                value={selectedTransaction?.notes}
                divClasses={TextDivStyles}
                pClasses={TextPStyles}
              />
            </div>
          </Modal>
        ) : null}
      </Modal>
    </>
  );
};

export default TransactionsModal;
